import queryString from 'query-string';
import { ApplicationResult } from '../utilities/post-notification-handler';

export enum ApplicationSuccessType {
  CODE = 'code',
  NONE = 'none',
  PAYMENT_REQUEST_ID = 'payment_request_id',
  BULK_PAYMENT_ID = 'bulk_payment_id',
  ACCOUNT_VERIFICATION_REPORT_ID = 'account_verification_report_id',
  BUSINESS_ACCOUNT_CHECK_REPORT_ID = 'business_account_verification_report_id',
  INCOME_CHECK_ID = 'income_check_id',
  EXPENSE_CHECK_ID = 'expense_check_id',
  REPORT_IDS = 'report_ids',
  RISK_INSIGHTS_ID = 'risk_insights_id',
  CONSENT_ID = 'consent_id',
}

type AdditionalInformation = {
  credentialsId?: string;
  customerReferenceId?: string;
  paymentRequestId?: string;
  accountVerificationReportId?: string;
};

type ApplicationSuccessResultArguments = {
  data?: string;
  successType: ApplicationSuccessType;
  additionalInformation?: AdditionalInformation;
};

export class ApplicationResultBuilder implements ApplicationResult {
  private data?: string;

  private successType: ApplicationSuccessType;

  private additionalInformation?: AdditionalInformation;

  constructor({ successType, data, additionalInformation }: ApplicationSuccessResultArguments) {
    this.data = data;
    this.successType = successType;
    this.additionalInformation = additionalInformation;
  }

  buildQueryPath() {
    const response = {
      [this.successType]: this.data,
    };

    if (this.additionalInformation?.credentialsId) {
      response.credentialsId = this.additionalInformation.credentialsId;
    }

    if (this.additionalInformation?.paymentRequestId) {
      response.payment_request_id = this.additionalInformation.paymentRequestId;
    }

    if (this.additionalInformation?.customerReferenceId) {
      response.customer_reference_id = this.additionalInformation.customerReferenceId;
    }

    if (this.additionalInformation?.accountVerificationReportId) {
      response.account_verification_report_id =
        this.additionalInformation.accountVerificationReportId;
    }

    return queryString.stringify(response);
  }

  buildIframeMessage() {
    const response = {
      type: this.successType,
      data: this.data,
      additionalInformation: this.additionalInformation,
    };

    return JSON.stringify(response);
  }
}

export type PayPayload = {
  paymentRequestId: string;
  accountVerificationReportId?: string;
};

type BulkPayPayload = {
  bulkPaymentId: string;
};

type BusinessAccountCheckPayload = {
  businessAccountCheckReportId: string;
};

type IncomeCheckPayload = {
  incomeCheckId: string;
};

type ExpenseCheckPayload = {
  expenseCheckId: string;
};

type RiskInsightsPayload = {
  riskInsightsId: string;
};

export const ApplicationSuccessResult = {
  pay: ({ accountVerificationReportId, paymentRequestId }: PayPayload) => {
    if (accountVerificationReportId) {
      return new ApplicationResultBuilder({
        successType: ApplicationSuccessType.ACCOUNT_VERIFICATION_REPORT_ID,
        data: accountVerificationReportId,
        additionalInformation: {
          paymentRequestId,
        },
      });
    }

    return new ApplicationResultBuilder({
      successType: ApplicationSuccessType.PAYMENT_REQUEST_ID,
      data: paymentRequestId,
    });
  },

  bulkPay: ({ bulkPaymentId }: BulkPayPayload) => {
    return new ApplicationResultBuilder({
      successType: ApplicationSuccessType.BULK_PAYMENT_ID,
      data: bulkPaymentId,
    });
  },

  payAccountCheck: ({ accountVerificationReportId, paymentRequestId }: PayPayload) => {
    return new ApplicationResultBuilder({
      successType: ApplicationSuccessType.ACCOUNT_VERIFICATION_REPORT_ID,
      data: accountVerificationReportId,
      additionalInformation: {
        paymentRequestId,
      },
    });
  },

  businessAccountCheck: ({ businessAccountCheckReportId }: BusinessAccountCheckPayload) => {
    return new ApplicationResultBuilder({
      successType: ApplicationSuccessType.BUSINESS_ACCOUNT_CHECK_REPORT_ID,
      data: businessAccountCheckReportId,
    });
  },

  incomeCheck: ({ incomeCheckId }: IncomeCheckPayload) => {
    return new ApplicationResultBuilder({
      successType: ApplicationSuccessType.INCOME_CHECK_ID,
      data: incomeCheckId,
    });
  },

  expenseCheck: ({ expenseCheckId }: ExpenseCheckPayload) => {
    return new ApplicationResultBuilder({
      successType: ApplicationSuccessType.EXPENSE_CHECK_ID,
      data: expenseCheckId,
    });
  },

  riskInsights: ({ riskInsightsId }: RiskInsightsPayload) => {
    return new ApplicationResultBuilder({
      successType: ApplicationSuccessType.RISK_INSIGHTS_ID,
      data: riskInsightsId,
    });
  },
};
